import {
  useState,
  type ChangeEvent,
  type FormEvent,
  type Dispatch,
  type SetStateAction,
} from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";

import "./ConnectForm.css"; 
import { firebaseApp } from "../firebase";
import generateToastContainer from "../utils/ToastContainer";
import { useTranslation } from "../../src/i18n/UseTranslation";
import type { Language } from "../i18n/UseTranslation";

function ResetPasswordForm() {
  const { t, language, setLanguage, availableLanguages } = useTranslation();

  const [email, setEmail] = useState<string>("");

  const [theme, setTheme] = useState<"light" | "dark">(
    () => (localStorage.getItem("app_theme") as "light" | "dark") ?? "light"
  );

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    localStorage.setItem("app_theme", next);
    document.documentElement.setAttribute("data-theme", next);
  };

  const auth = getAuth(firebaseApp);

  const resetPasswordMutation = useMutation<void, Error, string>({
    mutationKey: ["reset-password"],
    mutationFn: async (userEmail: string) => {
      await sendPasswordResetEmail(auth, userEmail.trim().toLowerCase());
    },
    onSuccess: () => {
      toast.success(t.auth.resetPasswordSuccess);
      setEmail(""); 
    },
    onError: (error: any) => {
      toast.error(error.message || t.auth.resetPasswordError);
    },
  });

  const handleInputChange =
    (state: Dispatch<SetStateAction<string>>) =>
    (event: ChangeEvent<HTMLInputElement>) =>
      state(event.target.value);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) {
      toast.error(t.auth.required || "Email is required");
      return;
    }
    resetPasswordMutation.mutate(email);
  };

  return (
    <div className={`connect-page connect-page--${theme}`}>
      {/* Contrôles Top-Right */}
      <div className="connect-controls">
        <select
          className="connect-lang-select"
          value={language}
          onChange={(e) => setLanguage(e.target.value as Language)}
          aria-label="Language"
        >
          {availableLanguages.map((l) => (
            <option key={l.code} value={l.code}>
              {l.label} {l.code.toUpperCase()}
            </option>
          ))}
        </select>

        <button
          className="connect-theme-btn"
          onClick={toggleTheme}
          aria-label={
            theme === "light" ? t.preferences.darkMode : t.preferences.lightMode
          }
        >
          {theme === "light" ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
          )}
        </button>
      </div>

      {/* Card Principale */}
      <div className="connect-card">
        <div className="connect-card-top-bar" />

        <div className="connect-card-body">
          {/* Brand */}
          <div className="connect-brand">
            <div className="connect-brand-icon">
              <img
                src="/logo_notacito.svg"
                alt="Notacito Logo"
                style={{ width: 35, height: 35 }}
              />
            </div>
            <span className="connect-brand-name">Notacito</span>
            <span className="connect-brand-tag">Admin</span>
          </div>

          <h1 className="connect-title">{t.auth.resetPasswordTitle}</h1>
          <p className="connect-subtitle" style={{ marginBottom: "32px" }}>
            {/* Si tu ajoutes une traduction pour le sous-titre un jour, mets-la ici. En attendant, on laisse un petit espace vide ou un texte par défaut. */}
          </p>

          <form onSubmit={handleSubmit} className="connect-form">
            <div className="connect-field">
              <label htmlFor="email">{t.auth.email}</label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="admin@company.com"
                value={email}
                onChange={handleInputChange(setEmail)}
                autoComplete="email"
                required
              />
            </div>

            <button
              type="submit"
              className="connect-btn-primary"
              disabled={resetPasswordMutation.isPending}
            >
              {resetPasswordMutation.isPending
                ? "..."
                : t.auth.sendResetEmail}
            </button>
          </form>

          {/* Lien retour à la connexion */}
          <div className="connect-register" style={{ marginTop: "28px" }}>
            <Link to="/" style={{ marginLeft: 0 }}>
              ← {t.auth.backToLogin}
            </Link>
          </div>
        </div>
      </div>

      {generateToastContainer()}
    </div>
  );
}

export default ResetPasswordForm;